/* Eastern-Time display helpers — the single place the UI turns a stored value
 * into something a human reads.
 *
 * Storage does not change: SYSUTCDATETIME()/GETUTCDATE() on the SQL side,
 * `datetime.now(timezone.utc)` in the services, ISO-8601 on the wire. What
 * changes here is the *display* edge. The UI renders in Syncon's own clock
 * (America/New_York) rather than whatever zone the viewer's machine is set to,
 * so a superintendent on a laptop still set to Pacific reads the same
 * "3:04 PM" a PM in Norfolk does.
 *
 * ── The part that matters: not every value is an instant ──────────────────
 *
 * The API sends three shapes, and only one of them may be timezone-converted:
 *
 *   "2026-08-19"                  SQL DATE (log_date, …). A calendar date. It
 *                                 has no time and no zone — it is the 19th in
 *                                 every zone on earth.
 *   "2026-08-19T00:00:00"         SQL DATETIME2 holding a *date*
 *                                 (start_date, due_date, completion_date …),
 *                                 serialized naive. Also a calendar date.
 *   "2026-08-19T15:04:00+00:00"   A real instant (created_at, updated_at …),
 *                                 tz-aware. THIS is what converts to Eastern.
 *
 * Running a calendar date through a timezone conversion shifts it a day, which
 * is how `new Date("2026-08-19").toLocaleDateString()` already renders "Aug 18"
 * for every user west of UTC — a live off-by-one this module removes. So we
 * classify first and only convert genuine instants.
 *
 * Dependency-free so the vitest suite can exercise it node-only.
 */

export const ET_TZ = 'America/New_York';

export type DateLike = string | number | Date | null | undefined;

/** `YYYY-MM-DD` with nothing after it. */
const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** `YYYY-MM-DD[T ]HH:MM[:SS[.fff]]` carrying no `Z` and no `±HH:MM` offset. */
const NAIVE_DT_RE =
  /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?$/;

export interface CivilParts {
  year: number;
  /** 1-12, *not* the 0-11 that Date.getMonth() returns. */
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

/** What a raw value represents.
 *  - `civil`  — a calendar date/time with no zone. Render verbatim.
 *  - `instant` — a real point in time. Convert to Eastern.
 *  - `null`   — empty or unparseable. */
type Classified =
  | { kind: 'civil'; parts: CivilParts; hasTime: boolean }
  | { kind: 'instant'; date: Date }
  | null;

function classify(v: DateLike): Classified {
  if (v === null || v === undefined || v === '') return null;

  if (v instanceof Date) {
    return Number.isNaN(v.getTime()) ? null : { kind: 'instant', date: v };
  }
  if (typeof v === 'number') {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : { kind: 'instant', date: d };
  }

  const s = v.trim();

  const dOnly = DATE_ONLY_RE.exec(s);
  if (dOnly) {
    return {
      kind: 'civil',
      hasTime: false,
      parts: {
        year: +dOnly[1], month: +dOnly[2], day: +dOnly[3],
        hour: 0, minute: 0, second: 0,
      },
    };
  }

  const naive = NAIVE_DT_RE.exec(s);
  if (naive) {
    return {
      kind: 'civil',
      hasTime: true,
      parts: {
        year: +naive[1], month: +naive[2], day: +naive[3],
        hour: +naive[4], minute: +naive[5], second: naive[6] ? +naive[6] : 0,
      },
    };
  }

  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : { kind: 'instant', date: d };
}

/** Parse to a Date when the value is a genuine instant. Calendar dates and
 *  unparseable input → null. Prefer the `et*` formatters over this. */
export function toInstant(v: DateLike): Date | null {
  const c = classify(v);
  return c && c.kind === 'instant' ? c.date : null;
}

/* Intl.DateTimeFormat construction is genuinely expensive and these render
 * once per row in tables and lists, so memoize per (options, zone) pair. */
const _fmtCache = new Map<string, Intl.DateTimeFormat>();

function formatter(opts: Intl.DateTimeFormatOptions, zoned: boolean): Intl.DateTimeFormat {
  const key = (zoned ? 'z:' : 'c:') + JSON.stringify(opts);
  let f = _fmtCache.get(key);
  if (!f) {
    // Locale stays `undefined` (the viewer's) — only the zone is pinned, so
    // date order and month names still follow the user's own conventions.
    f = new Intl.DateTimeFormat(undefined, zoned ? { ...opts, timeZone: ET_TZ } : opts);
    _fmtCache.set(key, f);
  }
  return f;
}

/** Default date shape used across the workspace: "Aug 19, 2026". */
export const DATE_MED: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
};

/** Default clock shape: "3:04 PM". */
export const TIME_SHORT: Intl.DateTimeFormatOptions = {
  hour: 'numeric',
  minute: '2-digit',
};

/** Render already-classified input. A civil value is formatted through a
 *  zone-less formatter over a local Date built from its own components, so
 *  what comes out is exactly what was stored. */
function render(c: Classified, opts: Intl.DateTimeFormatOptions): string | null {
  if (!c) return null;
  if (c.kind === 'instant') return formatter(opts, true).format(c.date);
  const p = c.parts;
  return formatter(opts, false).format(
    new Date(p.year, p.month - 1, p.day, p.hour, p.minute, p.second),
  );
}

/** Calendar date, e.g. "Aug 19, 2026". Instants are converted to Eastern;
 *  date-only values render as stored. Invalid/empty → null. */
export function etDate(v: DateLike, opts: Intl.DateTimeFormatOptions = DATE_MED): string | null {
  return render(classify(v), opts);
}

/** Time of day, e.g. "3:04 PM". Instants are converted to Eastern.
 *  A date-only value has no time → null. Invalid/empty → null. */
export function etTime(v: DateLike, opts: Intl.DateTimeFormatOptions = TIME_SHORT): string | null {
  const c = classify(v);
  if (c && c.kind === 'civil' && !c.hasTime) return null;
  return render(c, opts);
}

/** Date + time, e.g. "Aug 19, 2026, 3:04 PM". The replacement for a bare
 *  `new Date(x).toLocaleString()`. A date-only value renders as just the date,
 *  since inventing "12:00 AM" for it would be a lie. Invalid/empty → null. */
export function etDateTime(
  v: DateLike,
  opts: Intl.DateTimeFormatOptions = { ...DATE_MED, ...TIME_SHORT },
): string | null {
  const c = classify(v);
  if (c && c.kind === 'civil' && !c.hasTime) return render(c, DATE_MED);
  return render(c, opts);
}

/* ------------------------------------------------------------------ *
 * Calendar components — for grouping and date arithmetic              *
 * ------------------------------------------------------------------ */

/** Fixed en-US numeric formatter. We parse its parts and never show them, so
 *  the locale is pinned to keep `formatToParts` output predictable. */
const PARTS_FMT = new Intl.DateTimeFormat('en-US', {
  timeZone: ET_TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

/** The value's calendar and clock components as Eastern observes them.
 *  Instants are converted; calendar dates are returned as stored. */
export function etParts(v: DateLike): CivilParts | null {
  const c = classify(v);
  if (!c) return null;
  if (c.kind === 'civil') return c.parts;

  const out: Record<string, number> = {};
  for (const p of PARTS_FMT.formatToParts(c.date)) {
    if (p.type !== 'literal') out[p.type] = Number(p.value);
  }
  return {
    year: out.year,
    month: out.month,
    day: out.day,
    // `hour12: false` yields hour 24 for midnight on some engines; normalize.
    hour: out.hour % 24,
    minute: out.minute,
    second: out.second,
  };
}

/** A Date whose **local** components equal the value's **Eastern** components.
 *
 *  Use this for calendar arithmetic and week/month bucketing, where you need
 *  `getDay()`/`setDate()` to behave as they would in Norfolk. Two rules:
 *
 *    - Its epoch value is NOT the original instant. Never send one back to the
 *      API, compare it against a real timestamp, or store it.
 *    - Format it with plain `toLocale*` (no timeZone), never with the `et*`
 *      helpers above — those would convert a second time.
 */
export function etCivil(v: DateLike): Date | null {
  const p = etParts(v);
  if (!p) return null;
  return new Date(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
}

/** "Now" as a civil Eastern Date. Same rules as `etCivil`. */
export function etNowCivil(): Date {
  return etCivil(new Date()) as Date;
}

const pad2 = (n: number) => String(n).padStart(2, '0');

/** Stable Eastern calendar-day key, "YYYY-MM-DD". Invalid → null. */
export function etDayKey(v: DateLike): string | null {
  const p = etParts(v);
  return p ? `${p.year}-${pad2(p.month)}-${pad2(p.day)}` : null;
}

/** Eastern calendar-month key, "YYYY-MM". Invalid → null. */
export function etMonthKey(v: DateLike): string | null {
  const p = etParts(v);
  return p ? `${p.year}-${pad2(p.month)}` : null;
}

/** Eastern calendar-quarter key, "YYYY-Qn". Invalid → null. */
export function etQuarterKey(v: DateLike): string | null {
  const p = etParts(v);
  return p ? `${p.year}-Q${Math.floor((p.month - 1) / 3) + 1}` : null;
}
